import ApiHandler from '@/api/base'

class CustomDashboardApiHandler extends ApiHandler {
  async createDashboard({ name = '', backgroundId = 0 } = {}) {
    const { data } = await this.http.post('/dashboard', {
      name,
      backgroundId,
      private: true
    })
    return data
  }

  async getDashboard({ id = null, moduleParams = [] } = {}) {
    const { data } = await this.http.post(`/dashboard/${id}`, {
      moduleParams
    })

    return data
  }

  async updateDashboard({
    id = null,
    name = '',
    backgroundId = 0,
    layoutTypeId,
    isPrivate = true
  } = {}) {
    const { data } = await this.http.put(`/dashboard/${id}`, {
      name,
      backgroundId,
      layoutTypeId,
      private: isPrivate
    })
    return data
  }

  async deleteDashboard({ id = null } = {}) {
    const { data } = await this.http.delete(`/dashboard/${id}`)
    return data
  }

  async addElementsToDashboard({ dashboardId = null, moduleId = null, elementIds = [] } = {}) {
    const { data } = await this.http.post(`dashboard/${dashboardId}/modules/${moduleId}/elements`, {
      elementIds
    })
    return data
  }

  async addElementToDashboard({ dashboardId = null, elementId = null } = {}) {
    return await this.addElementsToDashboard({ dashboardId, elementIds: [elementId] })
  }

  async getDashboardsForAddElements({ elementIds = [] } = {}) {
    const { data } = await this.http.get('/dashboard/toAddElements', {
      params: {
        capacity: elementIds.length
      }
    })
    return data
  }

  async removeElementFromModule({ dashboardId = null, elementId = null, moduleId } = {}) {
    const { data } = await this.http.delete(
      `dashboard/${dashboardId}/modules/${moduleId}/element`,
      {
        data: {
          dashboardId,
          elementId,
          moduleId
        }
      }
    )
    return data
  }
  async getDashboardElements({ searchString = null, limit = 20, excludeElementIds = [] } = {}) {
    const { data } = await this.http.get(`/dashboard/elements`, {
      params: {
        searchString,
        limit,
        excludeElementIds
      }
    })
    return data
  }

  async getDashboardList({
    searchString = null,
    ownerIds = null,
    sort = null,
    offset = 0,
    limit = 50
  } = {}) {
    const { data } = await this.http.get('/dashboard/all', {
      params: {
        searchString,
        ownerIds,
        sort,
        limit,
        offset
      }
    })
    return data
  }

  async getDashboardCount({
    ownerIds = [],
    searchString = null,
    pageSize = null,
    isPrivate = null
  } = {}) {
    const { data } = await this.http.get(`/dashboard/count`, {
      params: {
        ownerIds,
        searchString,
        pageSize,
        isPrivate
      }
    })
    return data
  }

  async getDashboardChart({ id = null, granulation = null, moduleId } = {}) {
    const { data } = await this.http.get(`/dashboard/${id}/modules/${moduleId}/chart`, {
      params: {
        granulation
      }
    })
    return data
  }

  async getShareCandidates({ dashboardId = null, searchString = null } = {}) {
    const { data } = await this.http.get(`/dashboard/shareCandidates`, {
      params: {
        dashboardId,
        searchString
      }
    })
    return data
  }
  async addToFavorites({ dashboardIds = null } = {}) {
    const { data } = await this.http.post(`/dashboard/favorites`, {
      dashboardIds
    })
    return data
  }
  async removeFromFavorites({ dashboardIds = null } = {}) {
    const { data } = await this.http.delete(`/dashboard/favorites`, {
      data: {
        dashboardIds
      }
    })
    return data
  }

  async duplicateDashboard({ dashboardId = null, newName = null, includeShare = false } = {}) {
    const { data } = await this.http.post(`/dashboard/duplicate`, {
      dashboardId,
      newName,
      includeShare
    })
    return data
  }

  async duplicateDashboardModule({ dashboardId = null, newName = null, moduleId } = {}) {
    const { data } = await this.http.post(
      `dashboard/${dashboardId}/modules/${moduleId}/duplicate`,
      {
        newName
      }
    )
    return data
  }

  async getDashboardUsers({ selectedIds = null, searchString } = {}, limit = 10) {
    const { data } = await this.http.get(`/dashboard/users`, {
      params: {
        selectedIds,
        searchString,
        limit
      }
    })
    return data
  }

  async setOrderForModule({ dashboardId = null, orderValue = null, moduleId } = {}) {
    const { data } = await this.http.post(`dashboard/${dashboardId}/modules/order`, {
      orderValue,
      moduleId
    })
    return data
  }

  async createModule({
    moduleTypeId,
    id,
    name,
    layoutTypeId = undefined,
    activeColumns = undefined,
    textValue = null
  }) {
    const { data } = await this.http.post(`/dashboard/${id}/modules`, {
      typeId: moduleTypeId,
      name,
      dashboardId: id,
      orderValue: -1e3,
      layoutTypeId,
      activeColumns,
      textValue
    })
    return data
  }

  async getModule({ id, moduleId }) {
    const { data } = await this.http.get(`dashboard/${id}/modules/${moduleId}`)
    return data
  }

  async updateModuleAttribute({
    id,
    moduleId,
    layoutTypeId = undefined,
    activeColumns = undefined,
    granulationId = undefined,
    columnCount = undefined
  }) {
    const { data } = await this.http.put(`/dashboard/${id}/modules/${moduleId}`, {
      layoutTypeId,
      activeColumns,
      granulationId,
      columnCount
    })
    return data
  }

  async updateModule({
    id,
    moduleId,
    name = null,
    layoutTypeId = null,
    description = null,
    granulationId = null,
    columnCount = null,
    textValue = null
  }) {
    const { data } = await this.http.put(`/dashboard/${id}/modules/${moduleId}`, {
      name,
      layoutTypeId,
      description,
      granulationId,
      columnCount,
      textValue
    })
    return data
  }

  async addElementsToModule({ dashboardId, moduleId, elementIds }) {
    const { data } = await this.http.post(`dashboard/${dashboardId}/modules/${moduleId}/elements`, {
      dashboardId,
      moduleId,
      elementIds
    })
    return data
  }

  async deleteDashboardModule({ id = null, moduleId } = {}) {
    const { data } = await this.http.delete(`/dashboard/${id}/modules/${moduleId}`)
    return data
  }

  async setOrderForElement({ dashboardId, moduleId, elementId, orderValue } = {}) {
    const { data } = await this.http.post(`/dashboard/${dashboardId}/modules/${moduleId}/order`, {
      elementId,
      orderValue
    })
    return data
  }

  async getProgressModuleData({
    id = undefined,
    moduleId = undefined,
    viewPointLastDate = undefined,
    columnCount = 4,
    shiftSize = 0,
    granulationId = undefined
  }) {
    const { data } = await this.http.get(`/dashboard/${id}/modules/${moduleId}`, {
      params: {
        viewPointLastDate,
        columnCount,
        shiftSize,
        granulationId
      }
    })

    return data
  }

  async updateMembers({
    dashboardId = undefined,
    isPrivate = undefined,
    newOwnerAccountId = null,
    groups = [],
    users = []
  } = {}) {
    const { data } = await this.http.post(`/dashboard/members`, {
      dashboardId,
      private: isPrivate,
      newOwnerAccountId,
      groups,
      users
    })

    return data
  }
}
export default CustomDashboardApiHandler
